import { CommunityInputOffer } from '../community-inputs/community-input.model';
/**
 * Ideas: feature requests, as `/api/v1/feature-requests` serves them, normalised.
 * See the backend plan `ideas-and-feature-requests-across-multisite-culture-and-cultureconnector.md`.
 *
 * "Ideas" is the word a reader sees; "feature request" is the record.
 */
export type FeatureRequestStatus =
  | 'received'
  | 'under_review'
  | 'queued'
  | 'in_progress'
  | 'paused'
  | 'released'
  | 'declined'
  | 'duplicate';

export const FEATURE_REQUEST_STATUSES: FeatureRequestStatus[] = [
  'received',
  'under_review',
  'queued',
  'in_progress',
  'paused',
  'released',
  'declined',
  'duplicate',
];

/**
 * A status's translation key. A status the shared `tracking` object already
 * names is translated there, not again in the `ideas` scope.
 */
export function featureRequestStatusKey(status: FeatureRequestStatus): string {
  return status === 'in_progress' ? 'tracking.in_progress' : 'ideas.status.' + status;
}

/**
 * The stage filter's choices: three plain stages, any number on at once. **The backend owns
 * what each stage means** — which statuses it covers (`FeatureRequestStage` in the backend) —
 * and the list sends the stage's name as `filter[stage]`, so this area and CultureConnector
 * cannot group statuses differently: the Ideas plan, decision 3.26. Each row still shows its
 * precise status.
 */
export type FeatureRequestStage = 'pending' | 'in_progress' | 'closed';

export const FEATURE_REQUEST_STAGES: { key: FeatureRequestStage; labelKey: string }[] = [
  { key: 'pending', labelKey: 'ideas.stage.pending' },
  { key: 'in_progress', labelKey: 'tracking.in_progress' },
  { key: 'closed', labelKey: 'ideas.stage.closed' },
];

/**
 * Each stage's Bootstrap colour, used wherever an idea's status or stage is shown — the listing's
 * badge, the detail's badge and the listing's lightbulb — so a stage always looks the same.
 * Requested by the product owner on 2026-10-06.
 */
export const FEATURE_REQUEST_STAGE_COLOURS: Record<FeatureRequestStage, 'warning' | 'success' | 'info'> = {
  pending: 'warning',
  in_progress: 'success',
  closed: 'info',
};

/** The badge classes for a stage; a stage the backend has not sent reads as pending. */
export function featureRequestStageBadge(stage: FeatureRequestStage | null): string {
  return 'text-bg-' + FEATURE_REQUEST_STAGE_COLOURS[stage ?? 'pending'];
}

/** The text colour class for a stage, for an icon drawn in its colour. */
export function featureRequestStageText(stage: FeatureRequestStage | null): string {
  return 'text-' + FEATURE_REQUEST_STAGE_COLOURS[stage ?? 'pending'];
}

/** What a reader sees first: what is still to come, not what is finished. */
export const DEFAULT_STAGES: FeatureRequestStage[] = ['pending', 'in_progress'];

/** Up to this many tags are offered as pills; beyond it, as a dropdown checklist. */
export const TAG_PILL_LIMIT = 15;

/**
 * A feature tag the tag filter offers: one the requests this list could show carry, counted
 * under every filter but the tag filter, named in the reader's language — the list's `meta.tags`.
 */
export interface FeatureRequestTag {
  slug: string;
  name: string;
  count: number;
}

/** A topic, feature tag or culture an idea is about, named in the reader's language. */
export interface FeatureRequestNamed {
  slug: string;
  name: string;
}

/** How an idea reached us. Anything but `self_service` is shown as a phrase, never a name. */
export type FeatureRequestOrigin = 'self_service' | 'meeting' | 'email' | 'support' | 'other';

/** A fact true for this reader here; translated as `ideas.relevance.<key>`. */
export interface FeatureRequestRelevance {
  key: string;
  count?: number;
}

/**
 * Where a translation of one idea into the reader's language stands, as its detail's
 * `meta.translation_state` reports it — the vocabulary news and articles use. Only an
 * approved idea's detail carries one: lists never ask for a translation, and a pending idea
 * is never translated (the Ideas plan, decision 3.31).
 */
export type FeatureRequestTranslationState = 'absent' | 'queued' | 'translating' | 'ready' | 'partial' | 'failed' | 'refused';

/**
 * The listing's 16:9 picture, resolved by the backend (`IdeaThumbnail`): the idea's own thumbnail,
 * else its first topic's, else its first tag's icon, else its first culture's flag, else none —
 * when the page draws a lightbulb in the stage's colour.
 */
export type FeatureRequestThumbnail =
  | { kind: 'image'; source: 'own' | 'topic'; image: { disk: string; path: string; alt_text: string | null } }
  | { kind: 'icon'; iconType: string | null; iconIdentifier: string | null; imgUrl: string | null }
  | { kind: 'flag'; hash: string; iso: string | null; name: string | null }
  | { kind: 'none' };

export interface FeatureRequest {
  id: number;
  slug: string;
  name: string | null;
  summary: string | null;
  /** The initiator's own words, never edited. Detail only. */
  originalRequest: string | null;
  /** Markdown. Detail only. */
  description: string | null;
  /** Markdown. Detail only. */
  solution: string | null;
  status: FeatureRequestStatus;
  /** The plain stage the backend puts the status in. */
  stage: FeatureRequestStage | null;
  statusReason: string | null;
  statusChangedAt: Date | null;
  createdAt: Date | null;
  /** Submitted, waiting for approval: seen only by its initiator and staff. */
  pending: boolean;
  visibility: 'public' | 'hub';
  platformWide: boolean;
  hub: { slug: string; name: string } | null;
  duplicateOf: string | null;
  followsOnFrom: string | null;
  origin: FeatureRequestOrigin;
  /** Who wrote it — sent only when `origin` is `self_service`. */
  initiatorName: string | null;
  likesCount: number;
  /** Whether this reader likes it. */
  liked: boolean;
  /** Whether this reader follows it: hears of status changes by email, and of comments in the bell. */
  following: boolean;
  commentsCount: number;
  relevance: FeatureRequestRelevance[];
  /** The applications it is about. Detail only; empty elsewhere. */
  topics: FeatureRequestNamed[];
  /** Its feature tags. Detail only; empty elsewhere. */
  tags: FeatureRequestNamed[];
  /** The national cultures it is about. Detail only; empty elsewhere. */
  cultures: FeatureRequestNamed[];
  can: { like: boolean; comment: boolean; follow: boolean; withdraw: boolean };
  requestedLocale: string | null;
  servedLocale: string | null;
  /** Detail only, and only for an approved idea; null otherwise. */
  translationState: FeatureRequestTranslationState | null;
  /** Whether a translation is on its way, so the page should ask again shortly. */
  worthPolling: boolean;
  /**
   * The inputs this reader is qualified to take, for the "I want to help" card — the Ideas
   * plan, decision 3.32. Detail only; empty for anyone not invited to help.
   */
  inputs: CommunityInputOffer[];
  /** The listing's picture. List only; `none` elsewhere. */
  thumbnail: FeatureRequestThumbnail;
}

export interface FeatureRequestPage {
  items: FeatureRequest[];
  meta: { current_page: number; last_page: number; per_page: number; total: number };
  /** Which submit paths this reader has: a public request, and the hubs they may create for. */
  canCreate: { public: boolean; hubs: { slug: string; name: string }[] };
  /** The hub filtered by, when there was one. */
  hub: { slug: string; name: string } | null;
  /** The tags to offer, most frequent first. */
  tags: FeatureRequestTag[];
}

export interface FeatureRequestQuery {
  stages?: FeatureRequestStage[];
  /** Feature tag slugs, matched as "any of". */
  tags?: string[];
  status?: FeatureRequestStatus[];
  hub?: string;
  mine?: boolean;
  /** Only the requests the reader follows. */
  following?: boolean;
  search?: string;
  sort?: '-created_at' | 'created_at' | '-likes';
  page?: number;
  pageSize?: number;
}

/** What a reader writes. The hub, when given, makes it private to that hub. */
export interface FeatureRequestDraft {
  name: string;
  originalRequest: string;
  hub?: string | null;
}

/** Why an Ideas request failed, as a translation key in the `ideas` scope. */
export interface FeatureRequestError {
  status: number;
  translationKey: string;
  detail?: string;
  /** The reader holds none of the roles that open Ideas; show the invitation to get involved. */
  notInvolved?: boolean;
}
